import React, { useRef, useEffect } from 'react';

const streamColors = ['#ffffff', '#e2e8f0', '#cbd5e1', '#f1f5f9', '#ffffff', '#e2e8f0'];

function bezierPoint(t, sx, sy, cp1x, cp1y, cp2x, cp2y, ex, ey) {
  const t2 = t * t, t3 = t2 * t;
  const mt = 1 - t, mt2 = mt * mt, mt3 = mt2 * mt;
  return {
    x: mt3 * sx + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * ex,
    y: mt3 * sy + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * ey,
  };
}

const SpeedHeroBg = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, animId, frame = 0;

    // Light streams
    const streams = [];
    function spawnStream() {
      const fromLeft = Math.random() > 0.5;
      const y = Math.random() * H;
      const color = streamColors[Math.floor(Math.random() * streamColors.length)];
      const speed = 4 + Math.random() * 8;
      streams.push({
        x: fromLeft ? -100 : W + 100, y,
        speed: fromLeft ? speed : -speed,
        length: 80 + Math.random() * 200,
        color, thickness: 0.5 + Math.random() * 1.5,
        opacity: 0.1 + Math.random() * 0.2, life: 0,
      });
    }

    // Burst particles
    const burstParticles = [];
    function spawnBurst(count) {
      const cx = W * 0.5, cy = H * 0.45;
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.5 + Math.random() * 2.5;
        burstParticles.push({
          x: cx + (Math.random() - 0.5) * 40,
          y: cy + (Math.random() - 0.5) * 20,
          vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
          color: streamColors[Math.floor(Math.random() * streamColors.length)],
          size: 1 + Math.random() * 2,
          life: 0, maxLife: 120 + Math.random() * 180, trail: [],
        });
      }
    }

    // Fiber optic curves
    const fibers = [];
    function initFibers() {
      fibers.length = 0;
      const count = 12 + Math.floor(W / 150);
      for (let i = 0; i < count; i++) {
        const fromLeft = Math.random() > 0.5;
        fibers.push({
          sx: fromLeft ? -50 : W + 50, sy: Math.random() * H,
          cp1x: W * 0.25 + (Math.random() - 0.5) * W * 0.3, cp1y: Math.random() * H,
          cp2x: W * 0.75 + (Math.random() - 0.5) * W * 0.3, cp2y: Math.random() * H,
          ex: fromLeft ? W + 50 : -50, ey: Math.random() * H,
          color: streamColors[Math.floor(Math.random() * streamColors.length)],
          opacity: 0.015 + Math.random() * 0.02,
          pulsePos: Math.random(),
          pulseSpeed: 0.002 + Math.random() * 0.004,
          pulseDir: fromLeft ? 1 : -1,
        });
      }
    }

    // Speed lines
    const speedLines = [];
    function spawnSpeedLine() {
      const fromLeft = Math.random() > 0.5;
      speedLines.push({
        x: fromLeft ? -20 : W + 20, y: Math.random() * H,
        speed: fromLeft ? (10 + Math.random() * 15) : -(10 + Math.random() * 15),
        length: 30 + Math.random() * 80,
        opacity: 0.03 + Math.random() * 0.06,
        life: 0, maxLife: 60 + Math.random() * 40,
        color: streamColors[Math.floor(Math.random() * streamColors.length)],
      });
    }

    // Ambient dots
    const dots = [];
    for (let i = 0; i < 50; i++) {
      dots.push({
        x: Math.random(), y: Math.random(),
        vx: (Math.random() - 0.5) * 0.0001,
        vy: (Math.random() - 0.5) * 0.00008,
        size: 0.5 + Math.random() * 1.5,
        color: streamColors[Math.floor(Math.random() * streamColors.length)],
        opacity: 0.03 + Math.random() * 0.05,
        pulse: Math.random() * Math.PI * 2,
      });
    }

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      if (!parent) return;
      W = parent.clientWidth;
      H = parent.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      initFibers();
    }

    function render() {
      frame++;
      ctx.clearRect(0, 0, W, H);

      // Fiber optic curves
      for (const f of fibers) {
        ctx.strokeStyle = f.color;
        ctx.globalAlpha = f.opacity;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(f.sx, f.sy);
        ctx.bezierCurveTo(f.cp1x, f.cp1y, f.cp2x, f.cp2y, f.ex, f.ey);
        ctx.stroke();

        f.pulsePos += f.pulseSpeed * f.pulseDir;
        if (f.pulsePos > 1.1) f.pulsePos = -0.1;
        if (f.pulsePos < -0.1) f.pulsePos = 1.1;

        const steps = 10;
        for (let s = 0; s < steps; s++) {
          const t = f.pulsePos - s * (0.06 / steps) * f.pulseDir;
          if (t < 0 || t > 1) continue;
          const pt = bezierPoint(t, f.sx, f.sy, f.cp1x, f.cp1y, f.cp2x, f.cp2y, f.ex, f.ey);
          ctx.fillStyle = f.color;
          ctx.globalAlpha = (1 - s / steps) * 0.25;
          ctx.shadowColor = f.color;
          ctx.shadowBlur = s === 0 ? 8 : 0;
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 1.5 + (1 - s / steps) * 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.shadowBlur = 0;
      }

      // Speed lines
      for (let i = speedLines.length - 1; i >= 0; i--) {
        const sl = speedLines[i];
        sl.x += sl.speed; sl.life++;
        if (sl.life > sl.maxLife) { speedLines.splice(i, 1); continue; }
        const fadeIn = Math.min(1, sl.life / 10);
        const fadeOut = Math.min(1, (sl.maxLife - sl.life) / 15);
        const alpha = sl.opacity * fadeIn * fadeOut;
        const grad = ctx.createLinearGradient(sl.x - sl.length * Math.sign(sl.speed), sl.y, sl.x, sl.y);
        grad.addColorStop(0, 'rgba(0,0,0,0)'); grad.addColorStop(1, sl.color);
        ctx.strokeStyle = grad; ctx.globalAlpha = alpha; ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sl.x - sl.length * Math.sign(sl.speed), sl.y);
        ctx.lineTo(sl.x, sl.y); ctx.stroke();
        ctx.fillStyle = sl.color; ctx.globalAlpha = alpha * 2;
        ctx.beginPath(); ctx.arc(sl.x, sl.y, 1, 0, Math.PI * 2); ctx.fill();
      }

      // Light streams
      for (let i = streams.length - 1; i >= 0; i--) {
        const s = streams[i];
        s.x += s.speed; s.life++;
        if ((s.speed > 0 && s.x - s.length > W + 50) || (s.speed < 0 && s.x + s.length < -50)) {
          streams.splice(i, 1); continue;
        }
        const tailX = s.x - s.length * Math.sign(s.speed);
        const grad = ctx.createLinearGradient(tailX, 0, s.x, 0);
        grad.addColorStop(0, 'rgba(0,0,0,0)'); grad.addColorStop(0.7, s.color); grad.addColorStop(1, '#ffffff');
        ctx.strokeStyle = grad; ctx.globalAlpha = s.opacity; ctx.lineWidth = s.thickness;
        ctx.beginPath(); ctx.moveTo(tailX, s.y); ctx.lineTo(s.x, s.y); ctx.stroke();
        ctx.fillStyle = s.color; ctx.globalAlpha = s.opacity * 1.5;
        ctx.shadowColor = s.color; ctx.shadowBlur = 10;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.thickness + 1, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
      }

      // Burst particles
      for (let i = burstParticles.length - 1; i >= 0; i--) {
        const p = burstParticles[i];
        p.x += p.vx; p.y += p.vy; p.vx *= 0.995; p.vy *= 0.995; p.life++;
        if (p.life > p.maxLife) { burstParticles.splice(i, 1); continue; }
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 8) p.trail.shift();
        const fade = 1 - p.life / p.maxLife;
        for (let t = 0; t < p.trail.length; t++) {
          ctx.fillStyle = p.color;
          ctx.globalAlpha = (t / p.trail.length) * 0.1 * fade;
          ctx.beginPath(); ctx.arc(p.trail[t].x, p.trail[t].y, p.size * (t / p.trail.length), 0, Math.PI * 2); ctx.fill();
        }
        ctx.fillStyle = p.color; ctx.globalAlpha = 0.3 * fade;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
      }

      // Ambient dots
      for (const d of dots) {
        d.x += d.vx; d.y += d.vy; d.pulse += 0.02;
        if (d.x < -0.02) d.x = 1.02; if (d.x > 1.02) d.x = -0.02;
        if (d.y < -0.02) d.y = 1.02; if (d.y > 1.02) d.y = -0.02;
        const pulse = Math.sin(d.pulse) * 0.3 + 0.7;
        ctx.fillStyle = d.color; ctx.globalAlpha = d.opacity * pulse;
        ctx.beginPath(); ctx.arc(d.x * W, d.y * H, d.size, 0, Math.PI * 2); ctx.fill();
      }

      ctx.globalAlpha = 1;

      // Vignette
      const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.2, W / 2, H / 2, H * 0.95);
      vig.addColorStop(0, 'rgba(0,0,0,0)'); vig.addColorStop(1, 'rgba(0,0,0,0.5)');
      ctx.fillStyle = vig; ctx.fillRect(0, 0, W, H);

      // Spawning
      if (frame % 6 === 0) spawnSpeedLine();
      if (frame % 20 === 0) spawnStream();
      if (frame % 150 === 0) spawnBurst(8 + Math.floor(Math.random() * 6));

      animId = requestAnimationFrame(render);
    }

    resize();
    render();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};

export default SpeedHeroBg;
